import React, { ReactElement, useState } from 'react';
import { Badge, Popover, Select, SelectProps } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isHasAccess } from 'features/validateAccess';
import { useOpenTimesheetPeriodStateMutation, useCloseTimesheetPeriodStateMutation } from 'entities/timesheet';

import { getTimesheetsReopenPermission, getTimesheetsClosePermission } from '../../Timesheets.permissions';
import { AccountRow } from '../../TimesheetsTable.types';
import { WarningPopoverContent } from '../WarningPopoverContent';

import styles from './AccountStatusCell.module.scss';

const AccountStatusCell = ({ record }: { record: AccountRow }): ReactElement => {
  const { t } = useTranslation('Timesheet');

  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') ?? '';
  const projectId = searchParams.get('projectId') ?? '';

  const canReopenTimesheet = isHasAccess(getTimesheetsReopenPermission(accountId, projectId));
  const canCloseTimesheet = isHasAccess(getTimesheetsClosePermission(accountId, projectId));

  const canReopenAccount = canReopenTimesheet && record.periodState === 'CLOSED';
  const canCloseAccount = canCloseTimesheet && record.periodState === 'OPEN';

  const isStatusReadOnly = !canReopenAccount && !canCloseAccount;
  const hasClosedChildProjects = record.children.some((project) => project.periodState === 'CLOSED');

  const [open, setOpen] = useState(false);
  const selectValue = record.periodState === 'OPEN' ? 'true' : 'false';

  const { mutate: openTimesheetPeriodStateMutation } = useOpenTimesheetPeriodStateMutation(record.periodId);
  const { mutate: closeTimesheetPeriodStateMutation } = useCloseTimesheetPeriodStateMutation(record.periodId);

  const onChange: SelectProps['onChange'] = (value) => {
    if (hasClosedChildProjects) {
      if (value === 'true') {
        openTimesheetPeriodStateMutation();
      } else {
        closeTimesheetPeriodStateMutation();
      }
    } else {
      setOpen(true);
    }
  };

  const handleOpenChange = (newOpen: boolean): void => {
    setOpen(newOpen);
  };

  return (
    <div className={styles.accountStatusCell}>
      <Badge status={selectValue === 'true' ? 'default' : 'success'} />
      {isStatusReadOnly ? (
        <span className={styles.statusLabel}>
          {record.periodState === 'OPEN'
            ? t('timesheetTable.select.opened.label')
            : t('timesheetTable.select.closed.label')}
        </span>
      ) : (
        <Popover
          open={open}
          placement="bottom"
          arrow={{ pointAtCenter: true }}
          onOpenChange={handleOpenChange}
          content={<WarningPopoverContent handleOk={() => setOpen(false)} title="accountWarningPopoverContent.text" />}
          trigger={open ? ['click'] : []}
        >
          <Select
            value={selectValue}
            className={classNames(styles.select, { [styles.openedStatusSelect]: selectValue === 'true' })}
            onChange={onChange}
          >
            <Select.Option value="true">{t('timesheetTable.select.opened.label')}</Select.Option>
            <Select.Option value="false">{t('timesheetTable.select.closed.label')}</Select.Option>
          </Select>
        </Popover>
      )}
    </div>
  );
};

export { AccountStatusCell };
